
import React from "react";
import Layout from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { useAllNursingHomes } from "@/hooks/use-all-nursing-homes";
import { GoogleMap } from "@/components/nursing-homes/GoogleMap";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbNavigation } from "@/components/ui/breadcrumb-list";
import { generateBreadcrumbSchema } from "@/lib/schema";

export default function AllNursingHomesMapPage() {
  const { data: allNursingHomes, isLoading, error } = useAllNursingHomes();

  // Create breadcrumbs for this page
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Nursing Homes", url: "/nursing-homes" },
    { name: "All Nursing Homes Map", url: "/nursing-homes/map" }
  ];

  // Generate breadcrumb schema
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);

  // Calculate the number of nursing homes safely
  const nursingHomesCount = allNursingHomes?.length || 0;

  return (
    <Layout>
      <SEOHead 
        title="All Nursing Homes in Ireland Map | Grandpal"
        description="Interactive map showing all nursing homes across Ireland. Find and compare locations, facilities, and services to find the perfect nursing home."
        canonicalUrl="/nursing-homes/map"
        schema={breadcrumbSchema}
      />
      
      <div className="bg-grandpal-beige py-12">
        <div className="container">
          <h1 className="text-4xl font-bold text-center mb-4">
            All Nursing Homes in Ireland
          </h1>
          <p className="text-xl text-grandpal-gray text-center mb-8">
            Explore our interactive map of nursing homes across Ireland
          </p>
        </div>
      </div>

      <div className="container py-12">
        <BreadcrumbNavigation items={breadcrumbs.map(b => ({ label: b.name, href: b.url }))} />
        
        <Card className="w-full mt-6 mb-8 overflow-hidden p-0">
          <div className="w-full h-[980px]"> {/* Increased from 700px by 40% */}
            {isLoading ? (
              <div className="w-full h-full bg-gray-100 animate-pulse flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin h-8 w-8 border-2 border-grandpal-green border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-grandpal-gray">Loading nursing homes...</p>
                </div>
              </div>
            ) : error ? (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <div className="text-center p-8">
                  <h3 className="text-xl font-semibold mb-2">Unable to load map</h3>
                  <p className="text-grandpal-gray">We're having trouble loading the nursing homes map. Please try again later.</p>
                </div>
              </div>
            ) : (
              <GoogleMap 
                address="Ireland"
                size="full"
                showCard={false}
                className="w-full h-full"
                nursingHomes={allNursingHomes}
                defaultExpanded={true}
                allNursingHomes={true}
              />
            )}
          </div>
        </Card>
        
        <div className="mt-8 text-center">
          <p className="text-lg">
            Click on any nursing home pin to view detailed information about that facility.
          </p>
          <p className="mt-2 text-grandpal-gray">
            Our interactive map shows {nursingHomesCount} nursing homes across all counties in Ireland.
          </p>
        </div>
      </div>
    </Layout>
  );
}
